import React from "react";
import { Link } from "react-router-dom";
import { useApp, CURRENCIES } from "@/context/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Logo";
import { Globe, Sun, Moon, SlidersHorizontal } from "lucide-react";

export const Header = ({ onOpenFilters }) => {
  const { t, lang, setLang, languages, currency, setCurrency, currencies, theme, toggleTheme } = useApp();
  const isDark = theme === "dark";
  const curSymbol = (CURRENCIES[currency]?.symbol || currency).trim() || currency;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[color:var(--c-glass)] border-b border-[color:var(--c-border)]"
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-14 lg:h-20 flex items-center justify-between gap-2 lg:gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 lg:gap-3 group min-w-0">
          <span className="flex-shrink-0 transition-transform duration-300 group-hover:-rotate-3">
            <Logo className="w-9 h-9 lg:w-11 lg:h-11" />
          </span>
          <span className="flex flex-col leading-none min-w-0">
            <span className="font-serif text-lg lg:text-2xl tracking-tight text-[color:var(--c-ink)] whitespace-nowrap">{t("brand")}</span>
            <span className="pv-label mt-1 hidden lg:block">{t("tagline")}</span>
          </span>
        </Link>

        <div className="flex items-center gap-1.5 lg:gap-3 flex-shrink-0">
          {/* Mobile-only categories / filters access */}
          {onOpenFilters && (
            <button
              data-testid="header-filters-trigger"
              onClick={onOpenFilters}
              aria-label={t("filters")}
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-none bg-[color:var(--c-surface)] border border-[color:var(--c-border)] text-[color:var(--c-ink)] active:scale-95 transition-transform"
            >
              <SlidersHorizontal size={16} />
            </button>
          )}

          {/* Language — symbol only on mobile, full label on desktop */}
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger
              data-testid="language-switcher"
              className="w-9 lg:w-[120px] h-9 lg:h-10 rounded-none bg-[color:var(--c-surface)] border-[color:var(--c-border)] text-xs lg:text-sm px-0 lg:px-3 justify-center lg:justify-between [&>svg]:hidden lg:[&>svg]:block"
            >
              <div className="lg:hidden flex items-center justify-center w-full text-[color:var(--c-ink2)]"><Globe size={16} /></div>
              <div className="hidden lg:flex items-center gap-2"><Globe size={15} className="text-[color:var(--c-ink2)]" /><SelectValue /></div>
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {languages.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Currency — symbol only on mobile, code on desktop */}
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger
              data-testid="currency-switcher"
              className="w-9 lg:w-[92px] h-9 lg:h-10 rounded-none bg-[color:var(--c-surface)] border-[color:var(--c-border)] text-xs lg:text-sm px-0 lg:px-3 justify-center lg:justify-between [&>svg]:hidden lg:[&>svg]:block"
            >
              <div className="lg:hidden flex items-center justify-center w-full font-semibold text-sm text-[color:var(--c-ink)]">{curSymbol}</div>
              <div className="hidden lg:block"><SelectValue /></div>
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {currencies.map((c) => (
                <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle dark mode"
            className="relative inline-flex items-center gap-2 h-9 lg:h-10 w-9 lg:w-auto justify-center lg:pl-3 lg:pr-4 rounded-full bg-[color:var(--c-accent)] text-white font-semibold text-xs tracking-[0.15em] uppercase overflow-hidden hover:brightness-110 active:scale-95 transition-[filter,transform] duration-200 shadow-[0_2px_0_rgba(0,0,0,0.15)]"
          >
            <span className="flex items-center justify-center transition-transform duration-500" style={{ transform: isDark ? "rotate(0deg)" : "rotate(180deg)" }}>
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </span>
            <span className="hidden lg:inline">{isDark ? "Light" : "Dark"}</span>
          </button>
        </div>
      </div>
    </header>
  );
};
